import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// mui components
import { Autocomplete, Box, debounce, IconButton, Typography } from '@mui/material';
// components
import BaseInput from 'components/inputs/base-input';
// api
import api from 'utils/api';
import { ISpotifyData } from 'utils/apiTypes';
import { IArtistOption, spotifyArtistsToOptions } from 'utils/helpers';
// config
import { ProfileStepField } from '../steps';
// icons
import { X } from 'lucide-react';
// styles
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

import {
  extractArtistId,
  fetchArtistPreview,
  IArtistPreview,
  ISelectedArtist,
} from './spotify-artist-preview';

const toArtistUrl = (id: string | number) => `https://open.spotify.com/artist/${id}`;

/**
 * Spotify URL input with artist-name search (same endpoint as the invite
 * form). A chosen artist renders as an avatar + name preview (the stored form
 * value stays the profile URL); a stored URL is resolved to the preview via
 * GET /spotify/artist/:id — silently falling back to the raw URL text when the
 * lookup fails. Pasting a URL directly also works (the backend normalizes it).
 * A profile that already HAS a stored URL shows it read-only (identity field);
 * search/edit is offered only while the stored value is empty.
 */
const SpotifyUrlField: React.FC<{
  field: ProfileStepField;
  defaultValue?: string;
  /** The profile's saved value — defaultValue may additionally hold an unsaved draft. */
  storedValue?: string | null;
}> = ({ field, defaultValue, storedValue }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [artistOptions, setArtistOptions] = useState<IArtistOption[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<ISelectedArtist | null>(null);

  // read-only once a value is SAVED; a restored draft stays editable/clearable
  const isLocked = !!storedValue?.trim();

  // resolve an already-stored URL/id into the avatar + name preview
  useEffect(() => {
    if (!defaultValue) return undefined;
    let cancelled = false;
    fetchArtistPreview(defaultValue).then((artist) => {
      if (!cancelled && artist) setSelectedArtist(artist);
    });
    return () => {
      cancelled = true;
    };
  }, [defaultValue]);

  // guards late debounced responses from re-opening the dropdown after the query changed
  const latestQuery = useRef('');

  /** Name queries hit the search endpoint; URLs/bare ids resolve to a single suggestion. */
  const searchArtists = useMemo(
    () =>
      debounce(async (query: string) => {
        try {
          const id = extractArtistId(query);
          let options: IArtistOption[];
          if (id) {
            const artist = await api.spotify.getArtistById<IArtistPreview>({ id });
            options = artist?.name
              ? [
                  {
                    label: artist.name,
                    value: artist.id,
                    imageUrl: artist.imageUrl ?? undefined,
                    images: [],
                  },
                ]
              : [];
          } else {
            const data: ISpotifyData = await api.spotify.getSpotifyArtist({ id: query });
            options = spotifyArtistsToOptions(data);
          }
          if (latestQuery.current === query) setArtistOptions(options);
        } catch {
          if (latestQuery.current === query) setArtistOptions([]);
        }
      }, 300),
    []
  );

  return (
    <Controller
      name={field.key}
      control={control}
      defaultValue={defaultValue ?? ''}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) =>
        isLocked || selectedArtist ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              border: `1px solid ${colors.background.light.b4}`,
              borderRadius: '4px',
              p: '6px 8px',
              // matches the empty helper-text line the input fields reserve below themselves
              mb: '23px',
            }}
          >
            <Box
              component="a"
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                minWidth: 0,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {selectedArtist ? (
                <>
                  {selectedArtist.imageUrl && (
                    <Box
                      component="img"
                      src={selectedArtist.imageUrl}
                      alt={selectedArtist.name}
                      width="40px"
                      height="40px"
                      sx={{ borderRadius: '4px', objectFit: 'cover' }}
                    />
                  )}
                  <Typography sx={{ fontSize: 18 }}>{selectedArtist.name}</Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 18, color: '#6B6B6B' }}>{value}</Typography>
              )}
            </Box>
            {!isLocked && (
              <IconButton
                size="small"
                aria-label={t('Clear')}
                onClick={() => {
                  setSelectedArtist(null);
                  onChange('');
                }}
                sx={{ ml: 'auto' }}
              >
                <X size={18} />
              </IconButton>
            )}
          </Box>
        ) : (
          <Autocomplete
            freeSolo
            fullWidth
            options={artistOptions}
            filterOptions={(options) => options}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : toArtistUrl(option.value)
            }
            inputValue={value ?? ''}
            onInputChange={(_event, text, reason) => {
              // MUI fires an empty 'reset' on mount — don't wipe the stored value
              if (reason === 'reset' && !text) return;
              onChange(text);
              if (reason !== 'input') return;
              const query = text.trim();
              // names search; full URLs / bare ids resolve — partial URLs just clear
              if (query && (extractArtistId(query) || !query.includes('/'))) {
                latestQuery.current = query;
                searchArtists(query);
              } else {
                latestQuery.current = '';
                searchArtists.clear();
                setArtistOptions([]);
              }
            }}
            onChange={(_event, option) => {
              if (option && typeof option !== 'string') {
                onChange(toArtistUrl(option.value));
                setSelectedArtist({ name: option.label, imageUrl: option.imageUrl });
              }
            }}
            renderOption={(optionProps, option) => (
              // rectangular flush-left thumbnails, same as the invite form's AutocompleteInput
              <Box component="li" {...optionProps} key={option.value} sx={{ height: '52px' }}>
                {option.imageUrl && (
                  <Box
                    component="img"
                    width="52px"
                    height="52px"
                    src={option.imageUrl}
                    alt={option.label}
                    sx={{ marginLeft: '-16px', marginRight: '8px', objectFit: 'cover' }}
                  />
                )}
                {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <BaseInput
                {...params}
                variant="filled"
                hiddenLabel
                name={field.key}
                placeholder={field.placeholder ? t(field.placeholder) : undefined}
                error={!!error}
                helperText={error?.message || ' '}
                onBlur={onBlur}
              />
            )}
          />
        )
      }
    />
  );
};

export default SpotifyUrlField;
